/**
 * @file: useCheckAppDistUpdates.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { config } from '@euler/config';
import { NativeSupport } from '@euler/lib/native-support/NativeSupport.android';
import { AppVersion, AppVersionDetail } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import * as fs from 'expo-file-system';
import { FileSystemDownloadResult } from 'expo-file-system';
import * as Updates from 'expo-updates';
import { UpdateCheckResult } from 'expo-updates';
import { useCallback } from 'react';
import { Platform } from 'react-native';
import semver from 'semver';

export type AvailableUpdatesInfo =
  | {
      type: 'binary';
      head: AppVersionDetail;
      ota: {
        available: boolean;
        manifest?: Updates.Manifest;
      };
    }
  | {
      type: 'ota';
      manifest?: Updates.Manifest;
    };

export const useCheckAppDistUpdates = (force?: boolean) => {
  const { appDistService } = useServiceFactory();
  return useCallback(
    async (prerelease?: boolean): Promise<AvailableUpdatesInfo | null> => {
      if (Platform.OS === 'web') return null;

      const checkOtaUpdates = async (): Promise<UpdateCheckResult> => {
        if (__DEV__)
          return {
            isAvailable: false,
            manifest: undefined,
            // todo(eric): see if rollback to embedded is available
            // by consulting the new expo OTA updates spec
            isRollBackToEmbedded: false,
          };
        try {
          return await Updates.checkForUpdateAsync();
        } catch (e) {
          const errMsg = (e as Error)?.message;
          if (
            errMsg?.includes(
              'Multipart manifest response missing manifest part',
            )
          ) {
            return {
              isAvailable: false,
              manifest: undefined,
              isRollBackToEmbedded: false,
            };
          }
          throw e;
        }
      };

      if (Platform.OS === 'android') {
        const head = await appDistService.getVersionDetail({
          name: config.appDistName,
          platform: 'android',
          env: __DEV__
            ? 'development'
            : config.environment === 'production'
            ? 'production'
            : config.environment === 'staging'
            ? 'test'
            : 'development',
          version: 'HEAD',
          prerelease: prerelease ?? config.environment !== 'production',
        });

        if (
          head != null &&
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (force || semver.compare(config.version, head.version) < 0)
        ) {
          const otaUpdateCheckResult = await checkOtaUpdates();
          return {
            type: 'binary',
            head,
            ota: {
              available: __DEV__ ? false : otaUpdateCheckResult.isAvailable,
              manifest: otaUpdateCheckResult.manifest,
            },
          };
        }
      }

      const result = await checkOtaUpdates();
      if (result.isAvailable) {
        return { type: 'ota', manifest: result.manifest };
      }

      return null;
    },
    [appDistService, force],
  );
};

export type UpdateProgressEvent =
  | {
      type: 'initializing';
    }
  | {
      type: 'download-begin';
    }
  | {
      type: 'download-progress';
      progress: number;
    }
  | {
      type: 'download-complete';
    }
  | {
      type: 'download-error';
      error?: Error;
    }
  | {
      type: 'install-error';
      error?: Error;
    };

type UpdateProgressListener = (event: UpdateProgressEvent) => void;

export type UpdatesDownloadInstallResult =
  | {
      type: 'success';
    }
  | {
      type: 'continue';
      downloadedFile: string;
    }
  | {
      type: 'error';
      code?: 'download-error';
      error?: Error;
      message?: string;
    }
  | {
      type: 'not-supported';
    };

export const useDownloadAndInstallUpdates = (
  listener?: UpdateProgressListener,
) => {
  return useCallback(
    async (
      appVersion: AppVersion,
      downloadedFile?: string,
    ): Promise<UpdatesDownloadInstallResult> => {
      const packageUrl = appVersion.package;

      if (Platform.OS !== 'android') {
        return { type: 'not-supported' };
      }

      const install = async (
        path: string,
      ): Promise<UpdatesDownloadInstallResult> => {
        try {
          if (!(await NativeSupport.canRequestPackageInstalls())) {
            await NativeSupport.openManageUnknownAppSourcesSettings();
            return { type: 'continue', downloadedFile: path };
          }
          await NativeSupport.actionViewIntent(
            path,
            'application/vnd.android.package-archive',
          );
          return { type: 'success' };
        } catch (e) {
          listener?.({ type: 'install-error', error: e as Error });
          return {
            type: 'error',
            error: e as Error,
          };
        }
      };

      if (downloadedFile == null) {
        listener?.({ type: 'initializing' });

        const url = new URL(packageUrl);
        const filename = url.pathname.substring(
          url.pathname.lastIndexOf('/') + 1,
        );
        const cachedFileUri = fs.cacheDirectory + filename;

        const download = fs.createDownloadResumable(
          packageUrl,
          cachedFileUri,
          { cache: true },
          e => {
            const progress =
              e.totalBytesExpectedToWrite > 0
                ? e.totalBytesWritten / e.totalBytesExpectedToWrite
                : 0;
            listener?.({ type: 'download-progress', progress });
          },
        );

        listener?.({ type: 'download-begin' });

        let result: FileSystemDownloadResult | undefined = undefined;
        try {
          result = await download.downloadAsync();

          if (result == null) {
            listener?.({ type: 'download-error' });
            return { type: 'error', code: 'download-error' };
          }

          listener?.({ type: 'download-complete' });
        } catch (e) {
          listener?.({ type: 'download-error', error: e as Error });
          return { type: 'error', code: 'download-error', error: e as Error };
        }

        downloadedFile = result.uri.replace('file://', '');
      }

      return await install(downloadedFile);
    },
    [listener],
  );
};
